// @givmo/design-system — Popover (Base UI Popover).
//
// 03-3c overlay tier. A shared, anchored popover surface over @base-ui/react/popover. Base UI
// supplies the plumbing: anchor positioning (flip/shift), Escape + outside-click dismissal,
// focus restore to the trigger, and (when `modal`) a focus trap + scroll-lock. We theme only
// the surface from the 03-3a tokens (white card, gray60 1px border, radius all12, soft shadow).
//
// This is the shared anchored-overlay primitive the 03-3b <DatePicker> swaps onto (it used to
// ship a bespoke absolutely-positioned dropdown with hand-rolled dismiss handling — now it
// renders its calendar inside <Popover.Content>, keeping its public API + tests intact).
//
// a11y: <Popover.Trigger> wires `aria-haspopup`/`aria-expanded`/`aria-controls`; the popup
// carries role="dialog". Callers that need a labelled dialog render <Popover.Title>. `testId`
// forwards to the popup element on <Popover.Content>.
import type { ReactNode } from 'react';
import { Popover as BasePopover } from '@base-ui/react/popover';
import { cn } from '../lib/utils';
import { testIdProp, type TestIdProps } from '../lib/testid';

const PopoverRoot = BasePopover.Root;
const PopoverTrigger = BasePopover.Trigger;
const PopoverClose = BasePopover.Close;
const PopoverTitle = BasePopover.Title;
const PopoverDescription = BasePopover.Description;

export interface PopoverContentProps extends TestIdProps {
  children: ReactNode;
  /** Side of the trigger the popup is placed on (default 'bottom'). */
  side?: 'top' | 'bottom' | 'left' | 'right';
  /** Alignment relative to the trigger (default 'start'). */
  align?: 'start' | 'center' | 'end';
  /** Gap (px) between trigger and popup (default 4 — matches the kit's 4px dropdown gap). */
  sideOffset?: number;
  /**
   * Minimum gap (px) the popup keeps from the viewport edge when it has to shift to stay on screen.
   *
   * ⚠ IT IS WHAT MAKES A NEAR-FULL-WIDTH POPUP LOOK PLACED. A popup wider than the room its anchor
   * leaves gets shifted by the positioner, and the shift stops the instant it is on screen — so a
   * 351px popup in a 375px viewport landed 5px from one edge and 19px from the other, which reads as
   * an accident rather than as either anchored or centered. Setting the padding to the surface's own
   * gutter makes both insets that gutter.
   */
  collisionPadding?: number;
  /** Extra classes merged onto the popup card. */
  className?: string;
  /** Remove the default card chrome (border/padding) when the child supplies its own. */
  unstyled?: boolean;
  /**
   * Use the SaaS popup skin — surface fill, hairline border, shadow-md, 8px radius — the one
   * `Menu.Content saas` wears, so a popover beside a SaaS menu reads as the same system. ADDITIVE
   * (CBG-UX1, 2026-09-29): the default stays the legacy card, so no existing consumer changes.
   * Carries no padding: the caller spaces its own content. `unstyled` wins when both are set.
   */
  saas?: boolean;
  'aria-label'?: string;
}

/** The SaaS popup skin — the same surface, border and shadow as `Menu.Content saas`. */
const SAAS_POPUP = 'rounded-lg border border-border-subtle bg-surface shadow-md';

/** Portal + positioner + themed popup card, in one part. */
function PopoverContent({
  children,
  side = 'bottom',
  align = 'start',
  sideOffset = 4,
  collisionPadding,
  className,
  unstyled = false,
  saas = false,
  testId,
  'aria-label': ariaLabel,
}: PopoverContentProps) {
  return (
    <BasePopover.Portal>
      <BasePopover.Positioner
        side={side}
        align={align}
        sideOffset={sideOffset}
        collisionPadding={collisionPadding}
        className="z-50 outline-none"
      >
        <BasePopover.Popup
          aria-label={ariaLabel}
          className={cn(
            'outline-none',
            !unstyled &&
              (saas ? SAAS_POPUP : 'rounded-all12 border border-gray60 bg-white shadow-soft'),
            className,
          )}
          {...testIdProp(testId)}
        >
          {children}
        </BasePopover.Popup>
      </BasePopover.Positioner>
    </BasePopover.Portal>
  );
}

/** Compound Popover API: `<Popover.Root>…<Popover.Trigger>…<Popover.Content>`. */
export const Popover = {
  Root: PopoverRoot,
  Trigger: PopoverTrigger,
  Content: PopoverContent,
  Close: PopoverClose,
  Title: PopoverTitle,
  Description: PopoverDescription,
};
